// assets/nummer-veredelung.js
import { Component } from '@theme/component';

/**
 * Nummer-Veredelung.
 *
 * - Schaltet das Ziffern-Feld (Trikotnummer 0–99) per Toggle ein/aus.
 * - Der Wert wird als "#<Ziffern>" über ein verstecktes Feld am Hauptprodukt
 *   mitgesendet (das sichtbare Feld hat bewusst kein name-Attribut).
 * - Ist der Toggle aktiv, wird nach erfolgreichem "In den Warenkorb" automatisch
 *   das Aufpreis-Produkt (z. B. 2 €) zusätzlich in den Warenkorb gelegt.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} toggle
 * @property {HTMLInputElement} input
 * @property {HTMLInputElement} valueInput
 * @property {HTMLElement} field
 * @extends Component<Refs>
 */
class NummerVeredelung extends Component {
  #processing = false;

  connectedCallback() {
    super.connectedCallback();
    this.productId = this.dataset.productId;
    this.addonVariant = this.dataset.addonVariant;
    this.productTitle = this.dataset.productTitle || '';
    this.addonMultiplier = Number(this.dataset.addonMultiplier) || 1;
    this.productForm = document.getElementById('BuyButtons-ProductForm-' + this.dataset.sectionId);
    if (this.productForm) this.productForm.addEventListener('click', this.#onAddClick, true);
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.productForm) this.productForm.removeEventListener('click', this.#onAddClick, true);
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  /** Vor jedem Hinzufügen ein frisches Kennzeichen erzeugen (eindeutig pro Add). */
  #onAddClick = (event) => {
    const isAddToCart = event.target.closest('add-to-cart-component, .add-to-cart-button');
    if (!isAddToCart) return;
    const { toggle, refInput } = this.refs;
    if (!toggle?.checked) return;
    this.ref = 'num-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    if (refInput) {
      refInput.disabled = false;
      refInput.value = this.ref;
    }
  };

  /** Toggle umgeschaltet: Feld zeigen/verstecken und aktivieren/deaktivieren. */
  handleToggle() {
    const { toggle, input, field, refInput, valueInput } = this.refs;
    const on = toggle.checked;
    this.classList.toggle('is-active', on);
    field.hidden = !on;
    input.disabled = !on;
    input.required = on;
    if (on) {
      // eindeutiges Kennzeichen zur Verknüpfung mit der Aufpreis-Zeile
      this.ref = 'num-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      if (refInput) {
        refInput.disabled = false;
        refInput.value = this.ref;
      }
      if (valueInput) {
        valueInput.disabled = false;
        valueInput.value = input.value ? '#' + input.value : '';
      }
      input.focus();
    } else {
      input.value = '';
      this.ref = null;
      if (refInput) {
        refInput.disabled = true;
        refInput.value = '';
      }
      if (valueInput) {
        valueInput.disabled = true;
        valueInput.value = '';
      }
    }
  }

  /** Eingabe auf 1–2 Ziffern begrenzen und den versteckten "#"-Wert setzen. */
  handleInput() {
    const { input, valueInput } = this.refs;
    const digits = input.value.replace(/\D/g, '').slice(0, 2);
    input.value = digits;
    if (valueInput) valueInput.value = digits ? '#' + digits : '';
  }

  /**
   * Reagiert auf das erfolgreiche Hinzufügen des Hauptprodukts und legt
   * den Aufpreis-Artikel passend dazu in den Warenkorb.
   * @param {CustomEvent} event
   */
  #onCartUpdate = async (event) => {
    const data = event?.detail?.data;
    if (!data || data.source !== 'product-form-component' || data.didError) return;
    if (String(data.productId) !== String(this.productId)) return;

    const { toggle, input } = this.refs;
    if (!toggle?.checked) return;

    const digits = (input?.value || '').replace(/\D/g, '');
    if (digits.length < 1) return;

    if (!this.addonVariant) {
      console.warn('[Nummer-Veredelung] Kein Aufpreis-Produkt gefunden – Aufpreis wird nicht berechnet.');
      return;
    }

    if (this.#processing) return;
    this.#processing = true;

    const number = '#' + digits;
    const quantity = (Number(data.itemCount) || 1) * this.addonMultiplier;

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(this.addonVariant),
              quantity,
              properties: {
                'Veredelung für': this.productTitle,
                'Nummer': number,
                _ref: this.ref || '',
                _faktor: String(this.addonMultiplier),
              },
            },
          ],
        }),
      });

      // Warenkorb-Schublade / Warenkorb neu rendern lassen.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { source: 'nummer-addon', sections: {} } },
        })
      );
    } catch (error) {
      console.error('[Nummer-Veredelung] Aufpreis konnte nicht hinzugefügt werden:', error);
    } finally {
      setTimeout(() => {
        this.#processing = false;
      }, 400);
    }
  };
}

if (!customElements.get('nummer-veredelung-component')) {
  customElements.define('nummer-veredelung-component', NummerVeredelung);
}
